<template>
  <b-card>

    <b-form
      inline
      class="mb-2"
    >
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        class="d-inline-block mr-1 mb-1"
        @click="createNew"
      >
        {{ $t('Add') }}
      </b-button>

      <b-form-input
        v-model="searchTerm"
        :placeholder=" $t('message.SearchPlaceholder')"
        type="text"
        class="d-inline mb-1"
      />

      <b-dropdown
        v-if="selectedRecords.length>0"
        id="dropdown-actions"
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        :text="$t('Actions')"
        variant="primary"
        class="ml-1 mb-1"
      >
        <b-dropdown-item
          v-if="showStatusActions"
          variant="success"
          @click="setStatusSelectedRecords(true)"
        >
          <feather-icon
            icon="EyeIcon"
          />
          {{ $t('Set as Active') }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="showStatusActions"
          variant="warning"
          @click="setStatusSelectedRecords(false)"
        >
          <feather-icon
            icon="EyeOffIcon"
          />
          {{ $t('Set as Passive') }}
        </b-dropdown-item>
        <b-dropdown-divider v-if="showStatusActions" />
        <b-dropdown-item
          variant="danger"
          @click="confirmDelete"
        >
          <feather-icon
            icon="Trash2Icon"
          />
          {{ $t('Delete') }}
        </b-dropdown-item>
      </b-dropdown>

    </b-form>

    <!-- table -->
    <vue-good-table
      style-class="vgt-table striped"
      :columns="columns"
      :rows="rows"
      :sort-options="{
        enabled: true,
        initialSortBy: {field: 'id', type: 'desc'}
      }"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
      :select-options="{
        enabled: true,
        selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
        selectionInfoClass: 'custom-class',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true, // disable the select info panel on top
      }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength,
        mode: 'records',
        perPage: 10,
        perPageDropdown: [10, 50, 100],
        dropdownAllowAll: false,
      }"
      @on-selected-rows-change="selectedRowsChanged"
    >

      <template
        slot="table-column"
        slot-scope="props"
      >
        <span>
          {{ $t(props.column.label) }}
        </span>
      </template>

      <template
        slot="table-row"
        slot-scope="props"
      >

        <!-- Column: id -->
        <span
          v-if="props.column.field === 'id' && props.column.label !== 'ProjectId'"
          class="text-nowrap"
        >
          <router-link
            :to="{
              name: detailUrl,
              params: { id: props.row.id }
            }"
          >
            <feather-icon
              icon="SettingsIcon"
            />
          </router-link>

        </span>
        <!-- Column: switchUser -->
        <span
          v-else-if="props.column.field === 'switchUser' && props.row.role && (props.row.role==='user'||props.row.role==='poweruser')"
          class="text-nowrap"
        >
          <b-link @click="$emit('switchUser',props.row.id)">
            <feather-icon
              icon="EyeIcon"
            />
          </b-link>
        </span>

        <!-- Column: Common -->
        <span
          v-else
          :class="{'text-danger': 'status' in props.row && !props.row.status}"
        >
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

    </vue-good-table>

    <modal-item
      :item-type="section"
      @submit="loadData"
    />

  </b-card>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'

import {
  BForm, BCard, BFormInput, BButton, BDropdown, BDropdownItem, BDropdownDivider, BLink,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import ModalItem from './ModalItem.vue'

export default {
  components: {
    ModalItem,
    BDropdown,
    BDropdownItem,
    BForm,
    BCard,
    VueGoodTable,
    BFormInput,
    BButton,
    BDropdownDivider,
    BLink,
  },
  directives: {
    Ripple,
  },
  props: {
    section: {
      type: String,
      default: null,
    },
    detailUrl: {
      type: String,
      default: null,
    },
    columns: {
      type: Array,
      default: () => [],
    },
    showStatusActions: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      pageLength: 10,
      rows: [],
      searchTerm: '',
      selectedRecords: [],
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.$bvModal.hide('add-item')

      useJwt.axiosIns.get(this.section)
        .then(response => {
          if (response.data) {
            this.rows = response.data
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
    createNew() {
      this.$bvModal.show('add-item')
    },
    selectedRowsChanged(vals) {
      this.selectedRecords = vals.selectedRows.map(x => x.id)
    },

    confirmDelete() {
      this.$swal({
        title: 'Are you sure?',
        text: `You are about to delete ${this.selectedRecords.length} record(s)...`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deleteSelectedRecords()
        }
      })
    },

    deleteSelectedRecords() {
      const promises = this.selectedRecords.map(x => useJwt.axiosIns.delete(
        `${this.section}/${x}`,
      ))

      Promise.all(promises).then(() => {
        this.loadData()
        // show message
        this.$swal({
          icon: 'success',
          title: 'Deleted!',
          text: `${this.selectedRecords.length} record(s) has been deleted.`,
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      })
    },

    setStatusSelectedRecords(status) {
      const promises = this.selectedRecords.map(x => useJwt.axiosIns.patch(
        `${this.section}/${x}/setStatus/${status}`,
      ))

      Promise.all(promises).then(() => {
        this.loadData()
        // show message
        this.$swal({
          icon: 'success',
          title: 'Success!',
          text: `${this.selectedRecords.length} record(s) has been set status:${status}.`,
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      })
    },
  },
}
</script>

<style lang="scss" >
@import '@core/scss/vue/libs/vue-good-table.scss';
@import '@core/scss/vue/libs/vue-select.scss';
</style>
